import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { PasswordInput } from '../components/ui/password-input';
import { Wallet, Clock, CreditCard, Sparkles, Info, Shield, Zap, Globe, Target, AlertCircle, CheckCircle, RefreshCw, FileText, Award } from 'lucide-react';
import BackButton from '../components/BackButton';
import { useB2BWallet } from './useB2BWallet';
import B2BWalletTabs from './B2BWalletTabs';

const B2BWalletDashboard = () => {
  const {
    walletData, ledgerData, impactData, packages, services, invoices, labelData,
    loading, selectedTab, setSelectedTab, tenantId, setTenantId, apiKey, setApiKey,
    fetchWalletData, createOrder,
  } = useB2BWallet();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading B2B Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-600 text-white">
        <div className="container mx-auto px-4 py-8">
          <BackButton label="Back" className="text-white/80 hover:text-white mb-4" />

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Wallet className="h-8 w-8" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold">ESS Wallet Dashboard</h1>
                  <p className="text-blue-100 text-sm">Service Credits Management for B2B Partners</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Badge className="bg-white text-slate-800/20 text-white border-white/30 px-3 py-1 font-semibold">
                <Globe className="h-4 w-4 mr-1" />
                Tenant: {tenantId}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                onClick={fetchWalletData}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Balance Card */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 bg-gradient-to-br from-indigo-500 via-blue-500 to-cyan-500 text-white shadow-xl border-0">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-white">
                <Wallet className="h-6 w-6" />
                Current Balance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                <div>
                  <div className="flex items-baseline gap-2">
                    <p className="text-5xl md:text-6xl font-bold">
                      {walletData?.balance_sc?.toFixed(0) || 0}
                    </p>
                    <span className="text-xl text-blue-200">SC</span>
                  </div>
                  <p className="text-blue-200 mt-1">Service Credits Available</p>

                  {walletData?.low_balance_alert && (
                    <div className="mt-3 flex items-center gap-2 bg-yellow-500/30 px-3 py-2 rounded-lg">
                      <AlertCircle className="h-5 w-5 text-yellow-300" />
                      <span className="text-yellow-100 text-sm font-medium">Low balance alert</span>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4 w-full md:w-auto">
                  <div className="bg-white/10 px-4 py-3 rounded-lg backdrop-blur-sm">
                    <p className="text-blue-200 text-xs">Today's Usage</p>
                    <p className="text-2xl font-bold">{walletData?.limits?.consumed_today_sc || 0}</p>
                    <p className="text-blue-300 text-xs">/ {walletData?.limits?.daily_cap_sc || '∞'}</p>
                  </div>
                  <div className="bg-white/10 px-4 py-3 rounded-lg backdrop-blur-sm">
                    <p className="text-blue-200 text-xs">Monthly Usage</p>
                    <p className="text-2xl font-bold">{walletData?.limits?.consumed_this_month_sc || 0}</p>
                    <p className="text-blue-300 text-xs">/ {walletData?.limits?.monthly_cap_sc || '∞'}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Status Card */}
          <Card className="shadow-lg">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="h-5 w-5 text-green-600" />
                Wallet Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Status</span>
                <Badge className={
                  walletData?.status === 'active'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-red-100 text-red-700'
                }>
                  {walletData?.status === 'active' ? (
                    <><CheckCircle className="h-3 w-3 mr-1" /> Active</>
                  ) : (
                    <><AlertCircle className="h-3 w-3 mr-1" /> {walletData?.status}</>
                  )}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Subscription</span>
                <span className="font-medium">{walletData?.subscription_plan || 'Pay-per-use'}</span>
              </div>
              <div className="pt-3 border-t">
                <Button className="w-full bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700">
                  <CreditCard className="h-4 w-4 mr-2" />
                  Purchase Credits
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b pb-2 flex-wrap">
          {[
            { id: 'overview', label: 'Service Costs', icon: Zap },
            { id: 'ledger', label: 'Transactions', icon: Clock },
            { id: 'packages', label: 'Buy Credits', icon: Sparkles },
            { id: 'invoices', label: 'Invoices', icon: FileText },
            { id: 'impact', label: 'ESS Impact', icon: Target },
            { id: 'label', label: 'ESS Label', icon: Award }
          ].map(tab => (
            <Button
              key={tab.id}
              variant={selectedTab === tab.id ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setSelectedTab(tab.id)}
              className={selectedTab === tab.id ? 'bg-indigo-600' : ''}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Tab Content */}
        <B2BWalletTabs
          selectedTab={selectedTab}
          services={services}
          ledgerData={ledgerData}
          packages={packages}
          invoices={invoices}
          impactData={impactData}
          labelData={labelData}
          tenantId={tenantId}
          createOrder={createOrder}
        />

        {/* Tenant Configuration Input (for demo) */}
        <Card className="bg-gray-50">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Info className="h-4 w-4 text-gray-500" />
              Configuration (Demo Mode)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="text-xs text-gray-500 mb-1 block">Tenant ID</label>
                <input
                  type="text"
                  value={tenantId}
                  onChange={(e) => {
                    setTenantId(e.target.value);
                    localStorage.setItem('b2b_tenant_id', e.target.value);
                  }}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="partner_gcc_001"
                />
              </div>
              <div className="flex-1">
                <label className="text-xs text-gray-500 mb-1 block">API Key (optional)</label>
                <PasswordInput
                  value={apiKey}
                  onChange={(e) => {
                    setApiKey(e.target.value);
                    localStorage.setItem('b2b_api_key', e.target.value);
                  }}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="key_live_gcc_..."
                />
              </div>
              <div className="flex items-end">
                <Button onClick={fetchWalletData} size="sm">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Load
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default B2BWalletDashboard;
